import { Link, Text, tokens } from '@fluentui/react-components';
import { Map, type MapItem } from '@powerportalspro/react-maps';
import { useT } from '@powerportalspro/react';
import { useMemo, useState } from 'react';

// Reuses the Blazor demo's localization keys (MapMainDemo) so both stacks share strings.
const STRINGS_BASE = 'components.PowerPortalsPro.Demo.Client.Customizations.Pages.Maps.MapMainDemo';

// An office's details, carried as each marker's data and read back in the popup and clicks.
interface Office {
  kindKey: string;
  phone: string;
}

function marker(id: string, title: string, latitude: number, longitude: number, kindKey: string, phone: string): MapItem<Office> {
  return { kind: 'marker', id, title, position: { latitude, longitude }, data: { kindKey, phone } };
}

export function MapMainSample() {
  const t = useT();
  const [lastClick, setLastClick] = useState<string | null>(null);

  const items = useMemo<MapItem<Office>[]>(
    () => [
      marker('seattle', 'Seattle', 47.6062, -122.3321, 'head-office', '+1 206 555 0100'),
      marker('bellevue', 'Bellevue', 47.6101, -122.2015, 'sales-office', '+1 425 555 0101'),
      marker('kent', 'Kent', 47.3809, -122.2348, 'warehouse', '+1 253 555 0102'),
      marker('portland', 'Portland', 45.5152, -122.6784, 'sales-office', '+1 503 555 0103'),
      marker('san-francisco', 'San Francisco', 37.7749, -122.4194, 'sales-office', '+1 415 555 0104'),
      marker('denver', 'Denver', 39.7392, -104.9903, 'warehouse', '+1 303 555 0105'),
      marker('dallas', 'Dallas', 32.7767, -96.797, 'sales-office', '+1 214 555 0106'),
      marker('chicago', 'Chicago', 41.8781, -87.6298, 'warehouse', '+1 312 555 0107'),
      marker('atlanta', 'Atlanta', 33.749, -84.388, 'sales-office', '+1 404 555 0108'),
      marker('toronto', 'Toronto', 43.6532, -79.3832, 'sales-office', '+1 416 555 0109'),
      marker('new-york', 'New York', 40.7128, -74.006, 'sales-office', '+1 212 555 0110'),
      marker('boston', 'Boston', 42.3601, -71.0589, 'sales-office', '+1 617 555 0111'),
      {
        kind: 'polyline',
        id: 'route',
        title: t(`${STRINGS_BASE}.route`),
        color: 'var(--colorPaletteDarkOrangeBorderActive)',
        // Bellevue, Seattle and Kent, then down the coast through Portland to San Francisco.
        path: [
          { latitude: 47.6101, longitude: -122.2015 },
          { latitude: 47.6062, longitude: -122.3321 },
          { latitude: 47.3809, longitude: -122.2348 },
          { latitude: 47.2529, longitude: -122.4443 },
          { latitude: 47.0379, longitude: -122.9007 },
          { latitude: 46.7162, longitude: -122.9543 },
          { latitude: 46.1468, longitude: -122.9084 },
          { latitude: 45.5152, longitude: -122.6784 },
          { latitude: 44.9429, longitude: -123.0351 },
          { latitude: 44.0521, longitude: -123.0868 },
          { latitude: 42.439, longitude: -123.3284 },
          { latitude: 40.5865, longitude: -122.3917 },
          { latitude: 38.5816, longitude: -121.4944 },
          { latitude: 37.7749, longitude: -122.4194 },
        ],
      },
      {
        kind: 'circle',
        id: 'chicago-zone',
        title: t(`${STRINGS_BASE}.delivery-zone`),
        color: 'var(--colorPaletteGreenBorderActive)',
        center: { latitude: 41.8781, longitude: -87.6298 },
        radius: 40_000,
      },
      {
        kind: 'polygon',
        id: 'northeast',
        title: t(`${STRINGS_BASE}.sales-region`),
        color: 'var(--colorPalettePurpleBorderActive)',
        path: [
          { latitude: 45, longitude: -75 },
          { latitude: 45, longitude: -67 },
          { latitude: 41, longitude: -69.8 },
          { latitude: 39.5, longitude: -75.5 },
        ],
      },
    ],
    [t],
  );

  return (
    <>
      {/* No provider: the portal's own, from its PowerPortalsPro:Maps settings, with its keys. */}
      <Map<Office>
        items={items}
        height="480px"
        showPrintButton
        onItemClick={(event) => {
          setLastClick(event.item.title ?? null);
          // Lines and areas say what they are under the map in place of a popup: the page's own
          // answer to the click, which keeps the map from opening one.
          if (event.item.kind !== 'marker') event.preventDefault();
        }}
        onMapClick={({ position }) =>
          setLastClick(t(`${STRINGS_BASE}.map-clicked`, [position.latitude.toFixed(4), position.longitude.toFixed(4)]))
        }
        renderPopup={(item) => {
          const office = item.data as Office | undefined;
          return (
            <>
              <Text weight="semibold" block>
                {item.title}
              </Text>
              {office && (
                <>
                  <Text block style={{ color: tokens.colorNeutralForeground3 }}>
                    {t(`${STRINGS_BASE}.${office.kindKey}`)}
                  </Text>
                  <Link href={`tel:${office.phone.replace(/ /g, '')}`}>{office.phone}</Link>
                </>
              )}
            </>
          );
        }}
      />
      <p style={{ marginTop: 12 }}>
        <strong>{t(`${STRINGS_BASE}.clicked-label`)}:</strong> {lastClick ?? t(`${STRINGS_BASE}.nothing-clicked`)}
      </p>
    </>
  );
}
